import RasterSource from 'ol/source/Raster';
import GeomodTMS from './GeomodTMS';
import {
  DEFAULT_LIGHT_CONDITION, LAYERS_PARAM, LIGHT_CONDITION_PARAM, DATABASE_VERSION_PARAM,
} from './GeomodSourceUtils';
import { S52_CO_DEPDW, S52_CO_DEPMD, S52_CO_DEPVS, S52_CO_DEPIT } from '../utils/S52Colors';
import { LAYERS_PARAMETERS_UPDATED_EVENT_TYPE } from '../utils/events.js';

/**
 * @typedef {import("./GeomodTMS").GeomodTMSOptions & SpecificGeomodTileRasterTMSOptions} GeomodTileRasterTMSOptions
 */

/**
 * @typedef {Object} SpecificGeomodTileRasterTMSOptions
 * @property {import("ol/source/Raster").Operation} operation	Raster operation. The operation will be called with data from input sources and the output will be assigned to the raster source.
 * @property {Object} lib Functions that will be made available to operations run in a worker.
 * @property {Map<string,string>} [extraParameters] The extra parameters for the request exemple: AIS_SHOW_TRACE=false.
 */

export default class GeomodTileRasterTMS extends RasterSource {
  /**
     *
     * @param {GeomodTileRasterTMSOptions} options The source options.
     */
  constructor(options = {}) {
    let extraParametersOptions;
    if (options.extraParameters) {
      extraParametersOptions = {};
      const extraParameters = JSON.parse(localStorage.getItem('layerExtraParameters')) || {};
      for (const key of options.extraParameters) {
        extraParametersOptions[key] = extraParameters[key];
      }
    }

    const libs = { ...options.lib };
    for (const elmt in libs) {
      libs[elmt] = JSON.stringify(libs[elmt]);
    }

    const tmsOptions = { ...options };
    delete tmsOptions.extraParameters;
    const source = new GeomodTMS({
      ...tmsOptions,
      url: options.url,
      color: options.light || DEFAULT_LIGHT_CONDITION,
      interpolate: false,
    });
    const rasterOptions = {
      sources: [source],
      operation: options.operation,
      lib: libs,
    };

    super(rasterOptions);

    /**
         * The layer extra parameters.
         * @type {Object<string,string>}
         */
    this.params_ = {
      ...extraParametersOptions,
      [LAYERS_PARAM]: options.name,
      [LIGHT_CONDITION_PARAM]: options.light || DEFAULT_LIGHT_CONDITION,
    };

    /**
         * The TMS source.
         * @private
         * @type {GeomodTMS}
         */
    this.source_ = source;

    /**
         * The source tag
         * @private
         * @type {string}
         */
    this.tag_ = options.filter;

    this.on('beforeoperations', (event) => {
      // Do not change the data reference (event.data = {});
      const [safetyContour, depthContour] = this.params_.SAFETY_CONTOUR;
      const { data } = event;
      data.safetyContour = safetyContour;
      data.depthContour = depthContour;
      // tide in negative values
      data.tide = safetyContour - depthContour;
      data.lightCondition = this.params_[LIGHT_CONDITION_PARAM];
    });

    if (extraParametersOptions) {
      document.addEventListener(LAYERS_PARAMETERS_UPDATED_EVENT_TYPE, (e) => {
        const updateParams = {};
        const extraParameters = JSON.parse(localStorage.getItem('layerExtraParameters')) || {};
        for (const key in extraParametersOptions) {
          if (e.detail.parameters.includes(key)) {
            updateParams[key] = extraParameters[key];
          }
        }
        if (Object.keys(updateParams).length > 0) {
          this.updateParams(updateParams);
        }
      });
    }
  }

  /**
     * Get the user-provided params, i.e. those passed to the constructor through
     * the "params" option, and possibly updated using the updateParams method.
     * @return {Object} Params.
     */
  getParams() {
    return this.params_;
  }

  /**
     * Update the user-provided params.
     * @param {Object} params Params.
     */
  updateParams(params) {
    this.params_ = { ...this.params_, ...params };
    if (params[LIGHT_CONDITION_PARAM] || params[DATABASE_VERSION_PARAM]) {
      this.source_.updateParams({
        [LIGHT_CONDITION_PARAM]: this.params_[LIGHT_CONDITION_PARAM],
        [DATABASE_VERSION_PARAM]: this.params_[DATABASE_VERSION_PARAM],
      });
    }
    this.changed();
  }

  /**
     * Get the source tag.
     * @return {string} The source tag.
     */
  getTag() {
    return this.tag_;
  }

  /**
     * Refresh the layer by update ts param. A new map request will be launch.
     */
  refresh() {
    this.source_.refresh();
  }
}

/**
 * Repaint image from server. Replace water color according to safety contour.
 * @type {import("ol/source/Raster").Operation}
 */
export const PAINT_FIRST_GROUP = function (pixels, data) {
  let pixel = pixels[0];
  if (pixel[3] !== 0 && (pixel[2] === 0 || pixel[2] === 1)) {
    // if blue pixel is 0 or 1
    let drval1;
    if (pixel[2] === 0) {
      drval1 = pixel[0] + pixel[1] / 10;
    } else if (pixel[2] === 1) {
      drval1 = -pixel[0] - pixel[1] / 10;
    }

    if (drval1 >= data.depthContour) {
      pixel = S52_CO_DEPDW[data.lightCondition];
    } else if (drval1 >= data.safetyContour) {
      pixel = S52_CO_DEPMD[data.lightCondition];
    } else if (drval1 >= data.tide) {
      pixel = S52_CO_DEPVS[data.lightCondition];
    } else {
      pixel = S52_CO_DEPIT[data.lightCondition];
    }
  }

  return pixel;
};
